// Import memo helper from React
import { useMemo } from "react";



// This converts a value into a safe number
const toNumber = (value) => {

    // Convert the incoming value to a Number
    const parsedValue = Number(value);

    // Return zero if the conversion failed
    return Number.isFinite(parsedValue) ? parsedValue : 0;

};



// This finds a price row for a specific internal level
const findPriceRowByLevel = (rows = [], level) => {

    // Return the matching row or null
    return rows.find((row) => Number(row?.level) === Number(level)) || null;

};



// This builds a monthly equivalent price for a package using the active tax display mode
const getMonthlyEquivalent = (packageItem, showIncludingTax = true) => {

    // Get the duration in months
    const durationInMonths = toNumber(packageItem?.duration_in_months);

    // Get the visible package price based on the current tax mode
    const visiblePackagePrice = showIncludingTax
        ? toNumber(packageItem?.price_inc_tax)
        : toNumber(packageItem?.price_exc_tax);

    // If there is no valid duration return the visible full price
    if (durationInMonths <= 0) {

        // Return fallback value
        return visiblePackagePrice;

    }

    // Return the package price split across the duration
    return visiblePackagePrice / durationInMonths;

};



// This gets the correct visible plan price using the display mode
const getPackageVisiblePrice = (packageItem, showIncludingTax) => {

    // Return correct field based on toggle
    return showIncludingTax
        ? toNumber(packageItem?.price_inc_tax)
        : toNumber(packageItem?.price_exc_tax);

};



// This gets the correct visible rate-card price using the display mode
const getRateVisiblePrice = (priceRow, showIncludingTax) => {

    // Return correct field based on toggle
    return showIncludingTax
        ? toNumber(priceRow?.price_inc_tax)
        : toNumber(priceRow?.price_exc_tax);

};



// This calculates base + selected print areas total for a product at a specific level
const getProductTotalAtLevel = ({
    product,
    level,
    selectedPrintAreas,
    showIncludingTax,
}) => {

    // Find the base row for this product and level
    const baseRow = (product?.base_price || []).find((row) => Number(row?.level) === Number(level)) || null;

    // Get base visible price
    const baseTotal = showIncludingTax
        ? toNumber(baseRow?.price_inc_tax)
        : toNumber(baseRow?.price_exc_tax)

    // Build selected print area list
    const selectedAreas = Array.isArray(selectedPrintAreas) && selectedPrintAreas.length
        ? selectedPrintAreas
        : (product?.print_areas || []).map((printArea) => printArea?.role);

    // Sum print area costs
    const printAreasTotal = (product?.print_areas || []).reduce((runningTotal, printArea) => {

        // Check if this print area is selected
        const isSelected = selectedAreas.includes(printArea?.role);

        // Skip if not selected
        if (!isSelected) {

            // Return unchanged total
            return runningTotal;

        }

        // Find matching print price row
        const printRow = (printArea?.print_prices || []).find((row) => Number(row?.level) === Number(level)) || null;

        // Return updated total
        return runningTotal + (showIncludingTax ? toNumber(printRow?.price_inc_tax) : toNumber(printRow?.price_exc_tax));

    }, 0);

    // Return full total
    return baseTotal + printAreasTotal;

};



// This hook normalises all pricing and plan data for the page
export default function usePodPricingData({
    rateCard,
    subscriptionPackages,
    subscriptionLevels,
}) {

    
    // Build normalized subscription levels with package variants
    const normalizedPlans = useMemo(() => {

        // Get raw level rows safely
        const rawLevels = Array.isArray(subscriptionLevels?.results)
            ? subscriptionLevels.results
            : [];

        // Get raw package rows safely
        const rawPackages = Array.isArray(subscriptionPackages?.results)
            ? subscriptionPackages.results
            : [];

        // Build active packages only
        //const activePackages = rawPackages.filter((packageItem) => packageItem?.active === "Y");

        // Map all display levels into usable objects
        return rawLevels
            .map((levelItem) => {

                // Get current internal level number
                const level = toNumber(levelItem?.level);

                // Get packages matching this level
                const matchedPackages = rawPackages.filter((packageItem) => toNumber(packageItem?.level) === level);

                // Find free or none package
                const freePackage = matchedPackages.find((packageItem) => toNumber(packageItem?.duration_in_months) === 0) || null;

                // Find monthly package
                const monthlyPackage = matchedPackages.find((packageItem) => toNumber(packageItem?.duration_in_months) === 1) || null;

                // Find yearly package
                const yearlyPackage = matchedPackages.find((packageItem) => toNumber(packageItem?.duration_in_months) === 12) || null;

                // Return normalized level object
                return {
                    level,
                    name: levelItem?.name || "Unnamed Plan",
                    description: levelItem?.description || "",
                    settings: levelItem.settings || [],
                    packages: {
                        free: freePackage,
                        monthly: monthlyPackage,
                        yearly: yearlyPackage,
                    },
                };

            })
            .sort((a, b) => a.level - b.level);

    }, [subscriptionLevels, subscriptionPackages]);



    // Build normalized product rate card data
    const normalizedProducts = useMemo(() => {

        // Get raw results safely
        const rawProducts = Array.isArray(rateCard?.results)
            ? rateCard.results
            : [];



        // Normalize a product into the format used by the pricing page
        const normalizeProduct = (product) => {

            // Return the normalized product
            return {

                // Store the product SKU
                sku: product?.sku || "",

                // Store the product title
                title: product?.title || "Untitled Product",

                // Store the product base prices
                base_price: Array.isArray(product?.base_price)
                    ? product.base_price
                    : [],

                // Store the available print areas
                print_areas: Array.isArray(product?.print_areas)
                    ? product.print_areas
                    : [],

                // Normalize child products using exactly the same structure
                children: Array.isArray(product?.children)
                    ? product.children.map((childProduct) => normalizeProduct(childProduct))
                    : [],

            };

        };



        // Normalize every top-level product
        return rawProducts.map((product) => normalizeProduct(product));

    }, [rateCard]);



    // Build a lookup map for plan names by level
    const planNameByLevel = useMemo(() => {

        // Reduce plans into an object map
        return normalizedPlans.reduce((accumulator, plan) => {

            // Store plan name under level key
            accumulator[plan.level] = plan.name;

            // Return updated map
            return accumulator;

        }, {});

    }, [normalizedPlans]);



    // Return all helper utilities and normalized values
    return {
        normalizedPlans,
        normalizedProducts,
        planNameByLevel,
        toNumber,
        findPriceRowByLevel,
        getMonthlyEquivalent,
        getPackageVisiblePrice,
        getRateVisiblePrice,
        getProductTotalAtLevel,
    };

}



